import { LvfAppModule, LvfAppPlatform } from "@kiraah-shared/common";
import type { IdentityProfilData } from "../models/mobile-profile.models";
import { buildMobileModuleAccessFlags } from "../profile/helpers/mobile-module-access";
import { mobileResolveIdentityAccessFlags } from "../profile/helpers/mobile-operation-access";
import { mobilePath } from "../shell/mobile-routes.constants";

export const ACADEMY_MOBILE_APP_CODE = "academy-mobile";
export const LEGACY_ELEARNING_MOBILE_APP_CODE = "elearning-mobile";
const STUDENT_SURVEYS_ROUTE = mobilePath("elearning/student/surveys");
const MENTOR_SURVEYS_ROUTE = mobilePath("elearning/mentor/surveys");
const CONTACT_SURVEYS_ROUTE = mobilePath("elearning/contact/surveys");
const SUPERVISOR_SURVEYS_ROUTE = mobilePath("elearning/supervisor/surveys");
const STUDENT_SESSIONS_ROUTE = mobilePath("elearning/student/sessions");
const MENTOR_SESSIONS_ROUTE = mobilePath("elearning/mentor/sessions");
const CONTACT_SESSIONS_ROUTE = mobilePath("elearning/contact/sessions");
const SUPERVISOR_SESSIONS_ROUTE = mobilePath("elearning/supervisor/sessions");
const HUB_SESSIONS_ROUTE = mobilePath("elearning/sessions");
const CATALOG_ROUTE = mobilePath("elearning");

export function collectActiveApplicationMenuUrls(
  profileData: IdentityProfilData | null | undefined,
  activeTenantAppUid: string | null | undefined
): readonly string[] {
  if (!profileData || !activeTenantAppUid) {
    return [];
  }

  const activeApp = profileData.Applications.find((app) => String(app.Uid) === activeTenantAppUid);
  const urls: string[] = [];
  for (const menu of activeApp?.Menus ?? []) {
    if (menu.Platform === LvfAppPlatform.WEB) {
      continue;
    }
    for (const item of menu.Items ?? []) {
      if (item.Platform === LvfAppPlatform.WEB) {
        continue;
      }
      const url = item.Url?.trim();
      if (url) {
        urls.push(url);
      }
    }
  }
  return urls;
}

function normalizeComparableUrl(url: string): string {
  return url.split("?")[0].replace(/\/+$/, "").toLowerCase();
}

function menuIncludesSurveys(
  menuUrls: readonly string[],
  segment: "student/surveys" | "mentor/surveys" | "contact/surveys" | "supervisor/surveys"
): boolean {
  const needle = segment.toLowerCase();
  return menuUrls.some((url) => normalizeComparableUrl(url).includes(needle));
}

function menuIncludesSessions(
  menuUrls: readonly string[],
  segment: "student/sessions" | "mentor/sessions" | "contact/sessions" | "supervisor/sessions"
): boolean {
  const needle = segment.toLowerCase();
  return menuUrls.some((url) => normalizeComparableUrl(url).includes(needle));
}

/** Taskbar Sessions → hub ou liste participant (apprenant / formateur). */
export function resolveMobileElearningSessionsRoute(
  profileData: IdentityProfilData | null | undefined,
  activeTenantAppUid: string | null | undefined,
  activeAppCode: string | undefined
): string {
  const { isRoot, isAdmin } = mobileResolveIdentityAccessFlags(profileData?.Identity);
  if (isRoot || isAdmin) {
    return HUB_SESSIONS_ROUTE;
  }

  const menuUrls = collectActiveApplicationMenuUrls(profileData, activeTenantAppUid);
  const hasStudentSessions = menuIncludesSessions(menuUrls, "student/sessions");
  const hasMentorSessions = menuIncludesSessions(menuUrls, "mentor/sessions");
  const hasContactSessions = menuIncludesSessions(menuUrls, "contact/sessions");
  const hasSupervisorSessions = menuIncludesSessions(menuUrls, "supervisor/sessions");

  if (!hasStudentSessions && !hasMentorSessions && !hasContactSessions && !hasSupervisorSessions) {
    return HUB_SESSIONS_ROUTE;
  }

  if (hasMentorSessions && !hasStudentSessions && !hasContactSessions && !hasSupervisorSessions) {
    return MENTOR_SESSIONS_ROUTE;
  }

  if (hasContactSessions && !hasStudentSessions && !hasMentorSessions && !hasSupervisorSessions) {
    return CONTACT_SESSIONS_ROUTE;
  }

  if (hasSupervisorSessions && !hasStudentSessions && !hasMentorSessions && !hasContactSessions) {
    return SUPERVISOR_SESSIONS_ROUTE;
  }

  const studentAccess = buildMobileModuleAccessFlags(
    profileData,
    activeTenantAppUid,
    LvfAppModule.ELEARNING_STUDENT_SESSION
  );
  const mentorAccess = buildMobileModuleAccessFlags(
    profileData,
    activeTenantAppUid,
    LvfAppModule.ELEARNING_MENTOR_SESSION
  );
  const contactAccess = buildMobileModuleAccessFlags(
    profileData,
    activeTenantAppUid,
    LvfAppModule.ELEARNING_THIRD_PART_CONTACT_SESSION
  );
  const supervisorAccess = buildMobileModuleAccessFlags(
    profileData,
    activeTenantAppUid,
    LvfAppModule.ELEARNING_SUPERVISOR_SESSION
  );

  if (mentorAccess.hasReadAccess && !studentAccess.hasReadAccess && !contactAccess.hasReadAccess && !supervisorAccess.hasReadAccess) {
    return MENTOR_SESSIONS_ROUTE;
  }

  if (contactAccess.hasReadAccess && !studentAccess.hasReadAccess && !mentorAccess.hasReadAccess && !supervisorAccess.hasReadAccess) {
    return CONTACT_SESSIONS_ROUTE;
  }

  if (supervisorAccess.hasReadAccess && !studentAccess.hasReadAccess && !mentorAccess.hasReadAccess && !contactAccess.hasReadAccess) {
    return SUPERVISOR_SESSIONS_ROUTE;
  }

  if (hasStudentSessions) {
    return STUDENT_SESSIONS_ROUTE;
  }

  if (hasContactSessions) {
    return CONTACT_SESSIONS_ROUTE;
  }

  return HUB_SESSIONS_ROUTE;
}

/** Taskbar Quiz → catalogue E-Academy ou enquêtes participant. */
export function resolveMobileElearningQuizRoute(
  profileData: IdentityProfilData | null | undefined,
  activeTenantAppUid: string | null | undefined,
  activeAppCode: string | undefined
): string {
  const menuUrls = collectActiveApplicationMenuUrls(profileData, activeTenantAppUid);
  const hasStudentSurveys = menuIncludesSurveys(menuUrls, "student/surveys");
  const hasMentorSurveys = menuIncludesSurveys(menuUrls, "mentor/surveys");
  const hasContactSurveys = menuIncludesSurveys(menuUrls, "contact/surveys");
  const hasSupervisorSurveys = menuIncludesSurveys(menuUrls, "supervisor/surveys");

  if (!hasStudentSurveys && !hasMentorSurveys && !hasContactSurveys && !hasSupervisorSurveys) {
    return CATALOG_ROUTE;
  }

  if (hasMentorSurveys && !hasStudentSurveys && !hasContactSurveys && !hasSupervisorSurveys) {
    return MENTOR_SURVEYS_ROUTE;
  }

  if (hasContactSurveys && !hasStudentSurveys && !hasMentorSurveys && !hasSupervisorSurveys) {
    return CONTACT_SURVEYS_ROUTE;
  }

  if (hasSupervisorSurveys && !hasStudentSurveys && !hasMentorSurveys && !hasContactSurveys) {
    return SUPERVISOR_SURVEYS_ROUTE;
  }

  const studentAccess = buildMobileModuleAccessFlags(
    profileData,
    activeTenantAppUid,
    LvfAppModule.ELEARNING_STUDENT_SESSION
  );
  const mentorAccess = buildMobileModuleAccessFlags(
    profileData,
    activeTenantAppUid,
    LvfAppModule.ELEARNING_MENTOR_SESSION
  );
  const contactAccess = buildMobileModuleAccessFlags(
    profileData,
    activeTenantAppUid,
    LvfAppModule.ELEARNING_THIRD_PART_CONTACT_SESSION
  );
  const supervisorAccess = buildMobileModuleAccessFlags(
    profileData,
    activeTenantAppUid,
    LvfAppModule.ELEARNING_SUPERVISOR_SEQUENCE_PLAYER
  );

  if (mentorAccess.hasReadAccess && !studentAccess.hasReadAccess && !contactAccess.hasReadAccess && !supervisorAccess.hasReadAccess) {
    return MENTOR_SURVEYS_ROUTE;
  }

  if (contactAccess.hasReadAccess && !studentAccess.hasReadAccess && !mentorAccess.hasReadAccess && !supervisorAccess.hasReadAccess) {
    return CONTACT_SURVEYS_ROUTE;
  }

  if (supervisorAccess.hasReadAccess && !studentAccess.hasReadAccess && !mentorAccess.hasReadAccess && !contactAccess.hasReadAccess) {
    return SUPERVISOR_SURVEYS_ROUTE;
  }

  return STUDENT_SURVEYS_ROUTE;
}
